import SiteLink from 'components/shared/sitelink';
import PaymentIcons from 'components/shared/paymentIcons';
import { addLocaleToHref } from 'lib/utils/helpers';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import FooterLogo from 'components/shared/footer-logo';
import { useGlobalModules } from 'contexts';
import Logo from 'components/shared/logo';
import Expander from 'components/shared/expander';
import LocaleSelector from '../localeSelector';
import KlaviyoComponent from '../klaviyo';
import Social from '../social';
import styles from './footer.module.scss';

const TermsAndConditions = ({ termsMenu, locale, router }) => (
  <div className={styles.termsAndConditions}>
    <div className={styles.title}>{termsMenu.title}</div>
    <ul className={styles.links}>
      {termsMenu.links.filter(Boolean).map(({ externalLink, slug, title: linkTitle }) => (
        <li key={linkTitle}>
          <SiteLink
            external={externalLink}
            href={addLocaleToHref(slug, locale, router)}
            aria-label={linkTitle}
            className={styles.link}
          >
            {linkTitle}
          </SiteLink>
        </li>
      ))}
    </ul>
  </div>
);

const DesktopFooter = ({
  submenus,
  socialLinks,
  locale,
  settings,
  countrySelectorLocales,
  translations,
  pageData,
}) => {
  // Calculate how many columns we need based on having 2 items per column
  const columnCount = Math.ceil(submenus?.length / 2);

  // Create an array of columns
  const columns = [];

  // Distribute the submenus into columns, 2 per column
  for (let i = 0; i < columnCount; i++) {
    // Get the two items for this column
    const columnItems = submenus.slice(i * 2, i * 2 + 2);

    columns.push(
      <div key={`footer_column_${i}`} className={styles.menuColumn}>
        {/* Add locale selector at the begining of the last column */}
        {i === columnCount - 1 && (
          <div className={styles.countrySelector}>
            <span className={styles.countrySelectorTitle}>
              {translations?.footer?.countrySelectorLabel}
            </span>
            <LocaleSelector
              locale={locale}
              allLocales={countrySelectorLocales}
              pageData={pageData}
              className={styles.localeSelector}
            />
          </div>
        )}
        {columnItems.map((item, index) => (
          <div key={`footer_menu_${item.title || index}`} className={styles.menuItem}>
            {item?.title && (
              <span className={styles.menuTitle} id={`menu-section-${i * 2 + index}`}>
                {item.title}
              </span>
            )}
            {item?.links && item.links.length > 0 && (
              <div className={styles.links}>
                {item.links.map((link, linkIndex) => (
                  <SiteLink
                    key={`footer_menu_link_${link.title || linkIndex}`}
                    locale={locale}
                    href={addLocaleToHref(link.slug, locale)}
                    external={link.externalLink}
                    className={styles.link}
                  >
                    <span>{link.title}</span>
                  </SiteLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.desktopFooter}>
      <div className={styles.leftContainer}>
        <div className={styles.logo}>
          <SiteLink href="/" className={styles.logoLink} aria-label="Home">
            <Logo locale={locale} />
          </SiteLink>
        </div>
        <Social locale={locale} links={socialLinks} className={styles.socialIcons} />
        <KlaviyoComponent
          newsletter={{ formId: settings?.klaviyo?.newsletterFormId }}
          className={styles.klaviyoForm}
          aria-label="Newsletter subscription"
        />
        <div className={styles.certifiedLogo}>
          <SiteLink
            external="https://www.bcorporation.net/en-us/find-a-b-corp/company/keune-haircosmetics-bv/"
            aria-label="Certified B Corporation"
          >
            <FooterLogo />
          </SiteLink>
        </div>
      </div>

      <nav className={styles.footerMenu} aria-label="Footer menu">
        {columns}
      </nav>
    </div>
  );
};

const MobileFooter = ({
  locale,
  submenus,
  settings,
  socialLinks,
  countrySelectorLocales,
  translations,
  pageData,
}) => (
  <div className={styles.mobileFooter}>
    <div className={styles.logo}>
      <SiteLink href="/" className={styles.logoLink} aria-label="Home">
        <Logo locale={locale} />
      </SiteLink>
    </div>
    <Social locale={locale} links={socialLinks} className={styles.socialIcons} />
    <div role="navigation" aria-label="Footer mobile menu">
      {submenus?.map((item, index) => (
        <Expander
          title={item.title}
          key={`footer_mobile_submenu_${item.title || index}`}
          className={styles.footerMenuExpander}
        >
          <nav className={styles.submenu}>
            {item?.links?.map((link, linkIndex) => (
              <SiteLink
                key={`footer_mobile_menu_link_${link.title || linkIndex}`}
                locale={locale}
                href={addLocaleToHref(link.slug, locale)}
                external={link.externalLink}
                className={styles.link}
              >
                <span>{link.title}</span>
              </SiteLink>
            ))}
          </nav>
        </Expander>
      ))}
    </div>
    <KlaviyoComponent
      newsletter={{ formId: settings?.klaviyo?.newsletterFormId }}
      className={styles.klaviyoForm}
      aria-label="Newsletter subscription"
    />
    <div className={styles.mobileFooterBottom}>
      <div className={styles.countrySelector}>
        <span className={styles.countrySelectorTitle}>
          {translations?.footer?.countrySelectorLabel}
        </span>
        <LocaleSelector
          locale={locale}
          allLocales={countrySelectorLocales}
          pageData={pageData}
          className={styles.localeSelector}
        />
      </div>
      <div className={styles.certifiedLogo}>
        <SiteLink
          external="https://www.bcorporation.net/en-us/find-a-b-corp/company/keune-haircosmetics-bv/"
          aria-label="Certified B Corporation"
        >
          <FooterLogo />
        </SiteLink>
      </div>
    </div>
  </div>
);

const Footer = ({ locale, pageData }) => {
  const globalModules = useGlobalModules();
  const { navigation, settings, countrySelectorLocales, translations } = globalModules || {};

  const submenus = navigation?.footer?.[0]?.submenus || [];
  const socialLinks = navigation?.footer?.[1]?.submenus?.[0]?.links || [];
  const termsMenu = navigation?.footer?.[1]?.submenus?.[1] || [];
  const { paymentIcons } = globalModules?.media || {};

  const router = useRouter();

  return (
    <footer className={classNames(styles.footer)}>
      <MobileFooter
        submenus={submenus}
        socialLinks={socialLinks}
        settings={settings}
        locale={locale}
        translations={translations}
        countrySelectorLocales={countrySelectorLocales}
        pageData={pageData}
      />
      <DesktopFooter
        submenus={submenus}
        socialLinks={socialLinks}
        settings={settings}
        locale={locale}
        translations={translations}
        countrySelectorLocales={countrySelectorLocales}
        pageData={pageData}
      />
      <TermsAndConditions termsMenu={termsMenu} locale={locale} router={router} />
      {settings?.locale?.hasWebshop && (
        <PaymentIcons
          className={styles.footerIcons}
          icons={paymentIcons}
          iconHeight="4rem"
          iconWidth="6rem"
        />
      )}
    </footer>
  );
};

export default Footer;
